import {
    MutationCache,
    QueryCache,
    QueryClient,
    defaultShouldDehydrateQuery,
    isServer,
    type DefaultError,
    type DefaultedQueryObserverOptions,
    type Mutation,
    type QueryKey,
    type QueryObserverOptions,
} from '@tanstack/react-query';

import { getErrorNotification, showErrorNotification } from 'lib/api/errors/errorNotification';
import type { ServerError } from 'models';
import { isLowMemoryDevice } from 'utils/device';

// All ISR pages revalidate at ~120s, so use that as the default SSR staleTime on both
// server and client — keeps queries fresh enough to avoid an immediate client refetch
// after hydration without over-caching.
const DEFAULT_STALE_TIME_MS = 120 * 1000;
const ERROR_TOAST_DEDUPE_MS = 30 * 1000;

// Client-side inactive-query retention. Shortened on <=4 GB devices so unused query
// data is reclaimed sooner, trading a few extra refetches for a smaller heap.
const CLIENT_GC_TIME_MS = isLowMemoryDevice() ? 2 * 60 * 1000 : 5 * 60 * 1000;

const lastErrorToastBySource = new Map<string, { key: string; shownAt: number }>();

/**
 * On the server, force `gcTime: Infinity` for every query regardless of what the call site asked
 * for, so no query ever schedules a garbage-collection timer during SSR.
 *
 * Why this has to be a client override rather than a default: `defaultOptions.queries.gcTime`
 * below is the LOWEST-precedence source in `defaultQueryOptions`' merge (defaults <-
 * `setQueryDefaults` <- per-call options), so any query passing its own `gcTime` wins. Dozens of
 * queries do — several with multi-hour values — and none of them guard on `isServer`, because on
 * the client that value is exactly right.
 *
 * Why an unguarded finite `gcTime` is a leak and not just wasted memory: `Removable.scheduleGc()`
 * schedules a real `setTimeout`, and Node stamps the current value of EVERY AsyncLocalStorage
 * instance onto each async resource it creates. A timer created while rendering therefore pins
 * that request's whole graph — Next's work-unit store, React's Fizz and Flight requests, every
 * fetch `Response` and its stream buffers — for the full delay. A 2-hour `gcTime` parks one
 * request's render graph for 2 hours. A production heap snapshot showed 576 such timers on a
 * single worker, anchoring ~19.9% of the heap and 36.7% of all heap nodes.
 *
 * `Infinity` is the safe forcing value: `isValidTimeout()` rejects it so no timer is scheduled,
 * and `updateGcTime()` resolves via `Math.max`, so `Infinity` can never be lowered afterwards.
 *
 * REQUIRES that every server query client stays request-scoped, which is what makes disabling
 * collection free: the cache is built for one render and dropped once it is dehydrated, so it has
 * nothing to garbage-collect. `getQueryClient()` upholds this by constructing a new client on every
 * server call — but a CALLER can still break it by holding the result at module scope, which in a
 * `'use client'` component is evaluated once per server worker rather than once per request. Such a
 * client would be shared by every request that worker handles and, with collection disabled here,
 * would grow for the process lifetime. So `getQueryClient()` must be called inside a component body
 * or per-request function, never at module scope — see `app/components/Providers.tsx` for the
 * pattern, and `app/(trade)/layout.tsx` / `app/(main)/perps/[ticker]/PerpsTickerLayoutClient.tsx`
 * for two call sites that had to be moved in-body to satisfy this.
 *
 * Deliberately NOT done via `timeoutManager.setTimeoutProvider`: that singleton also backs
 * `notifyManager`, observer intervals and retry backoff, so a no-op provider would hang the
 * `retry` policy below instead of just skipping gc.
 */
class ServerQueryClient extends QueryClient {
    override defaultQueryOptions<
        TQueryFnData = unknown,
        TError = DefaultError,
        TData = TQueryFnData,
        TQueryData = TQueryFnData,
        TQueryKey extends QueryKey = QueryKey,
        TPageParam = never,
    >(
        options:
            | QueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey, TPageParam>
            | DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
    ): DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
        const defaultedOptions = super.defaultQueryOptions(options);
        // A finite per-query override otherwise schedules a Node timer that
        // inherits the request's AsyncLocalStorage stores and pins the entire
        // server render until the timer expires.
        defaultedOptions.gcTime = Infinity;
        return defaultedOptions;
    }
}

const isServerError = (error: unknown): error is ServerError => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'unauthorized' in error &&
        'forbidden' in error &&
        'invalid' in error &&
        'conflict' in error
    );
};

const shouldShowGlobalErrorToast = (meta: Record<string, unknown> | undefined, defaultValue: boolean): boolean => {
    return typeof meta?.globalErrorToast === 'boolean' ? meta.globalErrorToast : defaultValue;
};

type MutationToastSource = Pick<Mutation<unknown, unknown, unknown, unknown>, 'mutationId'>;

const getMutationToastSourceKey = (mutation: MutationToastSource): string => {
    return `mutation:${mutation.mutationId}`;
};

const maybeShowServerErrorToast = (
    error: unknown,
    sourceKey: string,
    meta: Record<string, unknown> | undefined,
    defaultGlobalErrorToast: boolean,
): void => {
    if (isServer || !shouldShowGlobalErrorToast(meta, defaultGlobalErrorToast)) return;

    const notification = getErrorNotification(error);
    if (notification?.type !== 'server') return;

    const lastToast = lastErrorToastBySource.get(sourceKey);
    const now = Date.now();
    if (lastToast?.key === notification.key && now - lastToast.shownAt < ERROR_TOAST_DEDUPE_MS) return;

    showErrorNotification(notification);
    lastErrorToastBySource.set(sourceKey, { key: notification.key, shownAt: now });
};

function makeQueryClient() {
    // See `ServerQueryClient` — on the server every query is forced to `gcTime: Infinity` so SSR
    // never schedules a gc timer that would pin the request's render graph.
    const QueryClientConstructor = isServer ? ServerQueryClient : QueryClient;

    return new QueryClientConstructor({
        queryCache: new QueryCache({
            onError: (error, query) => {
                maybeShowServerErrorToast(error, `query:${query.queryHash}`, query.meta, false);
            },
            onSuccess: (_data, query) => {
                lastErrorToastBySource.delete(`query:${query.queryHash}`);
            },
        }),
        mutationCache: new MutationCache({
            onError: (error, _variables, _context, mutation) => {
                maybeShowServerErrorToast(error, getMutationToastSourceKey(mutation), mutation.meta, true);
            },
            onSuccess: (_data, _variables, _context, mutation) => {
                lastErrorToastBySource.delete(getMutationToastSourceKey(mutation));
            },
        }),
        defaultOptions: {
            queries: {
                // With SSR, we set a default staleTime above 0 to avoid refetching
                // immediately on the client after hydration. All ISR pages revalidate
                // at ~120 seconds, so match that.
                staleTime: DEFAULT_STALE_TIME_MS,
                // Server-side gcTime defaults to Infinity for automatic cleanup
                // Client-side uses the specified gcTime (shortened on low-memory devices)
                gcTime: isServer ? Infinity : CLIENT_GC_TIME_MS,
                retry: (failureCount, error: unknown) => {
                    if (isServerError(error)) {
                        if (error?.unauthorized || error?.forbidden || error?.invalid || error?.conflict) {
                            return false;
                        }
                    }

                    if (failureCount >= 2) {
                        return false;
                    }

                    return true;
                },
                refetchOnWindowFocus: false,
            },
            mutations: {
                retry: 0,
            },
            dehydrate: {
                // Include pending queries in dehydration for streaming — but only ones that are
                // actually in flight. `status === 'pending'` alone is also true for every query
                // that merely got BUILT during the render and never fetched: `useQuery` creates a
                // cache entry even when `enabled` is false, so a single server render of a page
                // like /markets mounts ~66-109 client-component queries (a production snapshot
                // showed 4,953 across 75 renders) that sit at `status: 'pending'`,
                // `fetchStatus: 'idle'` with nothing to stream. Serializing those pushed dead
                // weight into every RSC payload and cached HTML entry for no benefit — the client
                // rebuilds them identically on mount.
                //
                // Adding the `fetchStatus === 'fetching'` conjunct keeps the streaming case this
                // was written for (a query the server started and the client should await) and
                // drops only the idle ones.
                shouldDehydrateQuery: (query) =>
                    defaultShouldDehydrateQuery(query) ||
                    (query.state.status === 'pending' && query.state.fetchStatus === 'fetching'),
            },
        },
    });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
    if (isServer) {
        // Server: always make a new query client
        return makeQueryClient();
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) browserQueryClient = makeQueryClient();
        return browserQueryClient;
    }
}
